import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Search,
  Heart,
  ShoppingCart,
  User,
  Upload,
  ChevronRight,
  Filter,
  Download,
  CheckCircle,
  CreditCard,
  Smartphone,
  Info,
  Layers,
  Zap,
  LayoutDashboard,
  LogOut,
  X,
} from "lucide-react";
import hero from "../images/hero.png";
import { UserCircleIcon } from "@heroicons/react/24/outline";
import { registerUser, loginUser, getMe } from "../api/auth";



// --- MOCK DATA ---
const CATEGORIES = [
  "Nature",
  "Architecture",
  "Cyberpunk",
  "Abstract",
  "Portrait",
  "Aerial",
];

const INITIAL_IMAGES = [
  // ── NATURE (5 images) ──────────────────────────────────────────
  {
    id: 1,
    name: "Emerald Peaks",
    category: "Nature",
    price: 49,
    dimensions: "8000x5000",
    format: "TIFF",
    date: "2024-01-12",
    url: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 2,
    name: "Misty Forest",
    category: "Nature",
    price: 39,
    dimensions: "7000x4667",
    format: "JPG",
    date: "2024-03-08",
    url: "https://images.unsplash.com/photo-1448375240586-882707db888b?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 3,
    name: "Aurora Dreams",
    category: "Nature",
    price: 59,
    dimensions: "6720x4480",
    format: "RAW",
    date: "2023-11-20",
    url: "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 4,
    name: "Desert Dunes",
    category: "Nature",
    price: 35,
    dimensions: "6000x4000",
    format: "JPG",
    date: "2024-02-14",
    url: "https://images.unsplash.com/photo-1509316785289-025f5b846b35?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 5,
    name: "Ocean Serenity",
    category: "Nature",
    price: 45,
    dimensions: "7360x4912",
    format: "TIFF",
    date: "2023-09-05",
    url: "https://images.unsplash.com/photo-1505118380757-91f5f5632de0?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 6,
    name: "Lavender Fields",
    category: "Nature",
    price: 42,
    dimensions: "5472x3648",
    format: "JPG",
    date: "2024-04-01",
    url: "https://images.unsplash.com/photo-1468581264429-2548ef9eb732?auto=format&fit=crop&w=800&q=80",
  },

  // ── ARCHITECTURE (5 images) ────────────────────────────────────
  {
    id: 7,
    name: "Concrete Jungle",
    category: "Architecture",
    price: 19,
    dimensions: "4500x3000",
    format: "PNG",
    date: "2023-08-22",
    url: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 8,
    name: "Glass Tower",
    category: "Architecture",
    price: 29,
    dimensions: "5184x3456",
    format: "JPG",
    date: "2024-01-30",
    url: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 9,
    name: "Spiral Staircase",
    category: "Architecture",
    price: 34,
    dimensions: "4928x3264",
    format: "JPG",
    date: "2023-07-15",
    url: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 10,
    name: "Modern Facade",
    category: "Architecture",
    price: 22,
    dimensions: "6016x4016",
    format: "PNG",
    date: "2024-03-18",
    url: "https://images.unsplash.com/photo-1511818966892-d7d671e672a2?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 11,
    name: "Cathedral Light",
    category: "Architecture",
    price: 38,
    dimensions: "5500x3667",
    format: "TIFF",
    date: "2023-10-22",
    url: "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 12,
    name: "Urban Symmetry",
    category: "Architecture",
    price: 27,
    dimensions: "4752x3168",
    format: "JPG",
    date: "2024-02-09",
    url: "https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=800&q=80",
  },

  // ── CYBERPUNK (5 images) ───────────────────────────────────────
  {
    id: 13,
    name: "Neon Horizon",
    category: "Cyberpunk",
    price: 29,
    dimensions: "6000x4000",
    format: "JPG",
    date: "2023-10-05",
    url: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 14,
    name: "Rain District",
    category: "Cyberpunk",
    price: 39,
    dimensions: "5760x3840",
    format: "JPG",
    date: "2023-12-01",
    url: "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 15,
    name: "Neon Alley",
    category: "Cyberpunk",
    price: 45,
    dimensions: "6240x4160",
    format: "RAW",
    date: "2024-01-08",
    url: "https://images.unsplash.com/photo-1519501025264-65ba15a82390?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 16,
    name: "Tokyo Nights",
    category: "Cyberpunk",
    price: 55,
    dimensions: "7360x4912",
    format: "TIFF",
    date: "2023-09-14",
    url: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 17,
    name: "Cyber Streets",
    category: "Cyberpunk",
    price: 33,
    dimensions: "5472x3648",
    format: "JPG",
    date: "2024-02-22",
    url: "https://images.unsplash.com/photo-1555400038-63f5ba517a47?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 18,
    name: "Hologram City",
    category: "Cyberpunk",
    price: 49,
    dimensions: "6720x4480",
    format: "PNG",
    date: "2023-11-11",
    url: "https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?auto=format&fit=crop&w=800&q=80",
  },

  // ── ABSTRACT (5 images) ────────────────────────────────────────
  {
    id: 19,
    name: "Ethereal Flow",
    category: "Abstract",
    price: 35,
    dimensions: "5500x5500",
    format: "JPG",
    date: "2024-02-01",
    url: "https://images.unsplash.com/photo-1541701494587-cb58502866ab?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 20,
    name: "Liquid Chrome",
    category: "Abstract",
    price: 42,
    dimensions: "6000x6000",
    format: "PNG",
    date: "2023-08-10",
    url: "https://images.unsplash.com/photo-1558591710-4b4a1ae0f04d?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 21,
    name: "Prism Burst",
    category: "Abstract",
    price: 28,
    dimensions: "5120x5120",
    format: "JPG",
    date: "2024-03-25",
    url: "https://images.unsplash.com/photo-1567359781514-81173b801d49?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 22,
    name: "Smoke & Color",
    category: "Abstract",
    price: 38,
    dimensions: "4800x4800",
    format: "TIFF",
    date: "2023-07-30",
    url: "https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 23,
    name: "Geometric Pulse",
    category: "Abstract",
    price: 31,
    dimensions: "5000x5000",
    format: "PNG",
    date: "2024-01-19",
    url: "https://images.unsplash.com/photo-1518640467707-6811f4a6ab73?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 24,
    name: "Neural Wave",
    category: "Abstract",
    price: 47,
    dimensions: "6400x6400",
    format: "JPG",
    date: "2023-10-28",
    url: "https://images.unsplash.com/photo-1507908708918-778587c9e563?auto=format&fit=crop&w=800&q=80",
  },

  // ── PORTRAIT (5 images) ────────────────────────────────────────
  {
    id: 25,
    name: "Golden Hour",
    category: "Portrait",
    price: 59,
    dimensions: "7000x4600",
    format: "RAW",
    date: "2023-12-15",
    url: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 26,
    name: "Soft Shadows",
    category: "Portrait",
    price: 49,
    dimensions: "6240x4160",
    format: "JPG",
    date: "2024-02-28",
    url: "https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 27,
    name: "Urban Soul",
    category: "Portrait",
    price: 44,
    dimensions: "5760x3840",
    format: "JPG",
    date: "2023-09-19",
    url: "https://images.unsplash.com/photo-1552374196-c4e7ffc6e126?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 28,
    name: "Crimson Mood",
    category: "Portrait",
    price: 65,
    dimensions: "7360x4912",
    format: "RAW",
    date: "2024-03-12",
    url: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 29,
    name: "Blue Reverie",
    category: "Portrait",
    price: 52,
    dimensions: "6720x4480",
    format: "TIFF",
    date: "2023-11-05",
    url: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 30,
    name: "Bold & Free",
    category: "Portrait",
    price: 48,
    dimensions: "5472x3648",
    format: "JPG",
    date: "2024-01-25",
    url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=800&q=80",
  },

  // ── AERIAL (5 images) ──────────────────────────────────────────
  {
    id: 31,
    name: "Cloud Drifter",
    category: "Aerial",
    price: 25,
    dimensions: "5200x3200",
    format: "JPG",
    date: "2023-11-30",
    url: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 32,
    name: "City Grid",
    category: "Aerial",
    price: 38,
    dimensions: "6016x4016",
    format: "JPG",
    date: "2024-02-05",
    url: "https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 33,
    name: "River Delta",
    category: "Aerial",
    price: 44,
    dimensions: "7000x4667",
    format: "TIFF",
    date: "2023-08-17",
    url: "https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 34,
    name: "Coastline View",
    category: "Aerial",
    price: 55,
    dimensions: "6720x4480",
    format: "RAW",
    date: "2024-03-30",
    url: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 35,
    name: "Mountain Pass",
    category: "Aerial",
    price: 49,
    dimensions: "5760x3840",
    format: "JPG",
    date: "2023-10-10",
    url: "https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: 36,
    name: "Island Drone",
    category: "Aerial",
    price: 62,
    dimensions: "7360x4912",
    format: "TIFF",
    date: "2024-01-03",
    url: "https://images.unsplash.com/photo-1501854140801-50d01698950b?auto=format&fit=crop&w=800&q=80",
  },
];

const PRICING_PLANS = [
  {
    name: "Creator",
    price: 19,
    features: ["10 Downloads/mo", "Standard License", "720p Access"],
    color: "#3B82F6",
  },
  {
    name: "Professional",
    price: 49,
    features: [
      "50 Downloads/mo",
      "Extended License",
      "4K High Res",
      "Priority Support",
    ],
    color: "#8B5CF6",
    popular: true,
  },
  {
    name: "Enterprise",
    price: 199,
    features: [
      "Unlimited Downloads",
      "Full Commercial Rights",
      "Custom Format Requests",
      "Dedicated Manager",
    ],
    color: "#EC4899",
  },
];

// --- STYLES ---
const CustomStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&display=swap');

    :root {
      --primary-gradient: linear-gradient(135deg, #FF3CAC 0%, #784BA0 50%, #2B86C5 100%);
      --glass: rgba(255, 255, 255, 0.08);
      --glass-border: rgba(255, 255, 255, 0.15);
    }

    * {
        box-sizing: border-box;
        font-family: Haffer, Haffer-fallback-1, Haffer-fallback-2,
        Helvetica, Arial, Fustat, sans-serif !important;
    }

    body {
        background-color: #ffffff;
        color: #111111;
        overflow-x: hidden;
        margin: 0;
        letter-spacing: -0.01em;
    }

    h1, h2, h3, h4, .brand-font {
      font-weight: 800;
    }

    .vivid-text {
      background: var(--primary-gradient);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    .glass-morphism {
      background: var(--glass);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border: 1px solid var(--glass-border);
    }

    .image-card {
      position: relative;
      overflow: hidden;
      border-radius: 12px;
      transition: all 0.5s cubic-bezier(0.23, 1, 0.32, 1);
    }

    .image-card:hover {
      transform: translateY(-8px) scale(1.02);
      box-shadow: 0 20px 40px rgba(0,0,0,0.4);
    }

    .image-overlay {
      position: absolute;
      inset: 0;
      background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
      opacity: 0;
      transition: opacity 0.3s ease;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      padding: 1.5rem;
    }

    .image-card:hover .image-overlay {
      opacity: 1;
    }

    .btn-vivid {
      background: var(--primary-gradient);
      padding: 12px 24px;
      border-radius: 30px;
      font-weight: 700;
      transition: all 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      cursor: pointer;
      border: none;
      color: white;
      text-transform: uppercase;
      font-size: 0.85rem;
      letter-spacing: 0.05em;
    }

    .btn-vivid:hover {
      transform: scale(1.05);
      box-shadow: 0 0 20px rgba(120, 75, 160, 0.5);
    }

    .nav-link {
      position: relative;
      opacity: 0.7;
      transition: all 0.3s ease;
      background: none;
      border: none;
      color: black;
      cursor: pointer;
      font-weight: 600;
      text-transform: uppercase;
      font-size: 0.8rem;
      letter-spacing: 0.05em;
    }

    .nav-link:hover, .nav-link.active {
      opacity: 1;
    }

    .nav-link.active::after {
      content: '';
      position: absolute;
      bottom: -6px;
      left: 0;
      width: 100%;
      height: 3px;
      background: var(--primary-gradient);
      border-radius: 4px;
    }

    @keyframes scroll-reveal {
      from { opacity: 0; transform: translateY(30px); }
      to { opacity: 1; transform: translateY(0); }
    }

    .reveal {
      animation: scroll-reveal 0.8s ease forwards;
    }

    .rainbow-border {
      position: relative;
      padding: 2px;
      background: linear-gradient(to right, #ff2400, #e81d1d, #e8b71d, #1de840, #1ddde8, #2b1de8, #dd00f3, #dd00f3);
      background-size: 400% 400%;
      animation: rainbow 5s ease infinite;
      border-radius: 14px;
    }

    @keyframes rainbow {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }

    .loader-bar {
      height: 3px;
      background: var(--primary-gradient);
      position: fixed;
      top: 0;
      left: 0;
      z-index: 9999;
      transition: width 0.3s ease;
    }

    input, textarea, select {
      font-family: 'Syne', sans-serif !important;
    }

    .brand {
        color: white;
    }

    ::-webkit-scrollbar { width: 8px; }
    ::-webkit-scrollbar-track { background: #0a0a0a; }
    ::-webkit-scrollbar-thumb { background: #333; border-radius: 10px; }
    ::-webkit-scrollbar-thumb:hover { background: #444; }
  `}</style>
);

// FIX 1: Added missing closing </div> and properly typed props
const AiGenerationPage = ({
  aiPrompt,
  setAiPrompt,
  aiImage,
  aiLoading,
  user,
  generateAiImage,
  navigate,
}) => {
  return (
    <div className="pt-40 px-10 pb-28">
      <div className="max-w-4xl mx-auto">
        <textarea
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          placeholder="Describe the image you want to generate."
          className="w-full h-40 p-6 rounded-[24px] border border-gray-300 mb-8 text-lg"
        />

        <button
          onClick={generateAiImage}
          disabled={aiLoading}
          className="btn-vivid px-10 py-4"
        >
          {aiLoading ? "Generating..." : "Generate Image"}
        </button>

        {/* FIX 2: Added alt prop to img tag (was missing alt attribute) */}
        {aiImage && <img src={aiImage} alt="AI Generated" className="mt-10 rounded-3xl" />}
      </div>
    </div>
  );
};


export default function App() {
  const [currentPage, setCurrentPage] = useState("home");
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState([]);
  const [savedImages, setSavedImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const searchInputRef = useRef(null);
  const [authName, setAuthName] = useState("");
  const [authEmail, setAuthEmail] = useState("");
  const nameInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const [activeField, setActiveField] = useState(null);
  const [activeTab, setActiveTab] = useState("gallery");
  const [paymentStatus, setPaymentStatus] = useState("idle");
  const [subscribed, setSubscribed] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiImage, setAiImage] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [authPassword, setAuthPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [images, setImages] = useState([]);

  const [myImages, setMyImages] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [price, setPrice] = useState("");
  const [dimensions, setDimensions] = useState("");


  useEffect(() => {
    fetch("http://localhost:5000/api/images")
      .then((res) => {
        if (!res.ok) throw new Error("Server error");
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setImages(data);
        } else {
          setImages(INITIAL_IMAGES);
        }
      })
      .catch(() => {
        // Backend not running — use mock data so UI still works
        setImages(INITIAL_IMAGES);
      });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    getMe(token)
      .then((res) => {
        setUser(res.data.user);
      })
      .catch((err) => {
        console.warn("Auth check failed:", err?.message || err);
        localStorage.removeItem("token");
      });
  }, []);

  useEffect(() => {
    if (activeField === "name" && nameInputRef.current) {
      nameInputRef.current.focus();
    }
    if (activeField === "email" && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [authName, authEmail, activeField]);

  useEffect(() => {
    if (currentPage === "explore" && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchQuery, currentPage]);

  useEffect(() => {
    if (localStorage.getItem("newsletter")) {
      setSubscribed(true);
    }
  }, []);

  const uploadImage = async () => {
    if (!file) {
      alert("Please select an image");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      alert("Please login first");
      return;
    }

    const formData = new FormData();
    formData.append("image", file);
    formData.append("title", title);
    formData.append("category", category);
    formData.append("price", price);
    formData.append("dimensions", dimensions);

    const res = await fetch("http://localhost:5000/api/images/upload", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await res.json();

    if (res.ok) {
      alert("Image uploaded successfully");
      setFile(null);
      setTitle("");
      setCategory("");
      setPrice("");
      setDimensions("");
    } else {
      alert(data.message || "Upload failed");
    }
  };


  const handleSubscribe = () => {
    localStorage.setItem("newsletter", "true");
    setSubscribed(true);
  };


  const navigate = (page, category = null) => {
    setIsLoading(true);

    if (page === "explore") {
      setSearchQuery("");
      setActiveCategory(category || "All");
    }

    setTimeout(() => {
      setCurrentPage(page);
      setIsLoading(false);
      window.scrollTo(0, 0);
    }, 500);
  };

  const addToCart = (img) => {
    if (!cart.find((i) => i.id === img.id)) {
      setCart((prev) => [...prev, img]);
    }
  };

  // FIX 3: addPayment was calling setUser with prev.payments but user may not have payments array initially
  const addPayment = (payment) => {
    setUser((prev) => ({
      ...prev,
      payments: [...(prev.payments || []), payment],
    }));
  };


  const handleRegister = async (e) => {
    e.preventDefault();
    setAuthError("");

    try {
      const res = await registerUser({
        name: authName,
        email: authEmail,
        password: authPassword,
      });

      localStorage.setItem("token", res.data.token);
      setUser(res.data.user);
      setAuthPassword("");
      navigate("dashboard");
    } catch (err) {
      setAuthError(err.response?.data?.message || "Registration failed");
    }
  };


  const switchAuthMode = (mode) => {
    setAuthMode(mode);
    setAuthName("");
    setAuthEmail("");
    setAuthPassword("");
    setAuthError("");
  };


  const handleLogin = async (e) => {
    e.preventDefault();

    console.log("LOGIN DATA:", authEmail, authPassword);

    if (!authEmail || !authPassword) {
      setAuthError("Email and password are required");
      return;
    }

    try {
      const res = await loginUser({
        email: authEmail.trim(),
        password: authPassword,
      });

      localStorage.setItem("token", res.data.token);
      setUser(res.data.user);
      setAuthPassword("");
      navigate("dashboard");
    } catch (err) {
      setAuthError(err.response?.data?.message || "Login failed");
    }
  };



  const generateAiImage = async () => {
    if (!aiPrompt.trim()) return alert("Please enter a prompt");

    if (!user || !user.isPro) {
      alert("AI Generation is available only for Pro users");
      navigate("pricing");
      return;
    }

    try {
      setAiLoading(true);

      const response = await fetch(
        "https://your-ai-api-endpoint.com/generate",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({
            prompt: aiPrompt,
          }),
        }
      );

      const data = await response.json();
      setAiImage(data.imageUrl);
    } catch (error) {
      alert("AI generation failed");
    } finally {
      setAiLoading(false);
    }
  };


  const toggleSave = (img) => {
    if (!user) return;

    setUser((prev) => {
      // FIX 4: Guard against savedImages being undefined on user object
      const savedImgs = prev.savedImages || [];
      const exists = savedImgs.some((i) => i.id === img.id);

      return {
        ...prev,
        savedImages: exists
          ? savedImgs.filter((i) => i.id !== img.id)
          : [...savedImgs, img],
      };
    });
  };

  const downloadImage = (img) => {
    if (!user) {
      alert("Please login to download images");
      return;
    }
    window.open(img.url, "_blank");
    setUser((prev) => ({
      ...prev,
      downloads: (prev.downloads || 0) + 1,
    }));
  };

  const handleUpload = async (imageData) => {
    const token = localStorage.getItem("token");

    const res = await fetch("http://localhost:5000/api/images/upload", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(imageData),
    });

    const savedImage = await res.json();
    setImages((prev) => [savedImage, ...prev]);
  };

  const filteredImages = useMemo(() => {
    return images.filter((img) => {
      if (!img || !img.category || !img.name) return false;
      const matchesCategory =
        activeCategory === "All" ||
        img.category.toLowerCase() === activeCategory.toLowerCase();

      const matchesSearch =
        img.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        img.category.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesCategory && matchesSearch;
    });
  }, [images, activeCategory, searchQuery]);

  const Header = () => (
    <header className="fixed left-1/2 -translate-x-1/2 w-4/5 z-50 glass-morphism py-5 px-10 flex justify-between items-center rounded-3xl">
      <div
        className="flex items-center gap-3 cursor-pointer"
        onClick={() => navigate("home")}
      >
        <div className="w-10 h-10 from-pink-500 via-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
          <Layers className="text-purple-900" size={32} />
        </div>
        <span className="brand-font text-3xl tracking-tighter">
          VIVID<span className="vivid-text">LENS</span>
        </span>
      </div>

      <nav className="hidden md:flex items-center gap-10">
        {["home", "explore", "pricing"].map((page) => (
          <button
            key={page}
            onClick={() => navigate(page)}
            className={`nav-link ${currentPage === page ? "active" : ""}`}
          >
            {page}
          </button>
        ))}
      </nav>

      <div className="flex items-center gap-7">
        <button
          onClick={() => navigate("explore")}
          className="hover:text-blue-400 transition-colors"
        >
          <Search size={22} />
        </button>
        <button
          onClick={() => navigate("cart")}
          className="relative hover:text-pink-400 transition-colors"
        >
          <ShoppingCart size={22} />
          {cart.length > 0 && (
            <span className="absolute -top-3 -right-3 bg-pink-500 text-gray-900 text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold">
              {cart.length}
            </span>
          )}
        </button>

        {user ? (
          <div className="flex items-center gap-5 border-l border-gray-200 pl-7">
            <button
              onClick={() => navigate("upload")}
              className="hidden sm:flex items-center gap-2 text-[10px] font-bold uppercase bg-white/10 px-4 py-2 rounded-full hover:bg-white/20 transition-all border border-white/5"
            >
              <Upload size={14} /> Upload
            </button>
            <button
              onClick={() => setCurrentPage("dashboard")}
              className="w-10 h-10 rounded-full overflow-hidden border-2 border-purple-500 hover:scale-110 transition-transform flex items-center justify-center"
            >
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <UserCircleIcon className="w-full h-full text-gray-500" />
              )}
            </button>
          </div>
        ) : (
          <button
            onClick={() => {
              setAuthMode("login");
              setAuthEmail("");
              setAuthPassword("");
              setAuthName("");
              setAuthError("");
              navigate("auth");
            }}
            className="btn-vivid"
          >
            Join Free
          </button>
        )}
      </div>
    </header>
  );

  const Footer = () => (
    <footer className="bg-[#080808] border-t border-white/5 pt-24 pb-12 px-10">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-16 mb-20">
        <div className="col-span-1 md:col-span-1">
          <div className="flex items-center gap-3 mb-8">
            <Layers className="text-purple-500" size={32} />
            <span className="brand text-3xl">
              VIVID<span className="vivid-text">LENS</span>
            </span>
          </div>
          <p className="text-gray-400 text-sm leading-relaxed font-medium">
            The world's most vibrant community of photographers and creators.
            Redefining stock photography through color and high-end aesthetics.
          </p>
        </div>
        <div>
          <h4 className="text-xs text-white font-black uppercase tracking-widest mb-8">
            Library
          </h4>
          <ul className="space-y-4 text-gray-300 text-sm font-semibold">
            <li
              onClick={() => navigate("popular")}
              className="hover:text-gray-400 cursor-pointer"
            >
              Popular Collections
            </li>
            <li
              onClick={() => navigate("free")}
              className="hover:text-gray-400 cursor-pointer"
            >
              Free Assets
            </li>
            <li
              onClick={() => navigate("trending")}
              className="hover:text-gray-400 cursor-pointer"
            >
              Trending Now
            </li>
            <li
              onClick={() => navigate("ai")}
              className="hover:text-gray-400 cursor-pointer transition-colors"
            >
              AI Generation
            </li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs font-black uppercase tracking-widest mb-8 text-white">
            Company
          </h4>
          <ul className="space-y-4 text-gray-300 text-sm font-semibold">
            <li
              onClick={() => navigate("about")}
              className="hover:text-gray-400 cursor-pointer transition-colors"
            >
              About Us
            </li>
            <li
              onClick={() => navigate("contributor")}
              className="hover:text-gray-400 cursor-pointer transition-colors"
            >
              Contributor Program
            </li>
            <li
              onClick={() => navigate("api")}
              className="hover:text-gray-400 cursor-pointer"
            >
              API Access
            </li>
          </ul>
        </div>
        <div>
          <h4 className="text-xs font-black uppercase tracking-widest mb-8 text-white">
            Newsletter
          </h4>
          <p className="text-gray-300 text-xs mb-6 font-semibold">
            Get weekly curated gems direct to your inbox.
          </p>
          {!subscribed ? (
            <div className="flex gap-4 mt-6">
              <input
                type="email"
                placeholder="Your email"
                className="flex-1 px-6 py-4 rounded-full border border-gray-200"
              />
              <button
                onClick={handleSubscribe}
                className="bg-white text-black px-8 py-4 rounded-full font-black uppercase tracking-widest border border-black hover:bg-gray-300 hover:text-black transition-all duration-300"
              >
                Join
              </button>
            </div>
          ) : (
            <p className="mt-6 text-lg font-black text-green-600">
              🎉 Thank you for subscribing!
            </p>
          )}
        </div>
      </div>
      <div className="max-w-7xl mx-auto pt-10 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6 text-[10px] text-gray-600 font-bold uppercase tracking-widest">
        <p>© 2026 VIVIDLENS. ALL RIGHTS RESERVED.</p>
        <div className="flex gap-10">
          <span className="hover:text-gray-900 cursor-pointer">
            Privacy Policy
          </span>
          <span className="hover:text-gray-900 cursor-pointer">
            Terms of Service
          </span>
          <span className="hover:text-gray-900 cursor-pointer">
            License Agreement
          </span>
        </div>
      </div>
    </footer>
  );

  // --- PAGES ---
  const CATEGORY_IMAGES = {
    Nature:
      "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=800&q=80",
    Architecture:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab",
    Cyberpunk: "https://images.unsplash.com/photo-1550745165-9bc0b252726f",
    Abstract: "https://images.unsplash.com/photo-1541701494587-cb58502866ab",
    Portrait: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d",
    Aerial: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4",
  };

  const HomePage = () => (
    <div className="pt-24">
      <section className="relative min-h-[80vh] w-4/5 mx-auto mt-16 flex items-center px-10 overflow-hidden rounded-[3rem]">
        <div className="absolute inset-0 z-0">
          <img
            src={hero}
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="absolute inset-0 z-10 backdrop-blur-1xl bg-black/20"></div>

        <div className="absolute top-0 right-0 w-2/3 h-full opacity-30 pointer-events-none z-20">
          <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-purple-600 blur-[180px] rounded-full animate-pulse"></div>
          <div
            className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-blue-600 blur-[180px] rounded-full animate-pulse"
            style={{ animationDelay: "1s" }}
          ></div>
        </div>

        {/* CONTENT */}
        <div className="relative z-30 max-w-5xl reveal">
          <h1 className="text-7xl md:text-9xl mb-8 leading-[0.9] tracking-tighter text-white">
            Elevate Your{" "}
            <span
              className="vivid-text italic"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Passion
            </span>{" "}
            With VividLens
          </h1>

          <p className="text-xl text-gray-200 mb-12 max-w-2xl leading-relaxed font-medium">
            Access over 40 million premium assets with the most intuitive
            filtering system ever designed. From deep neon blacks to vibrant
            sun-soaked pastels.
          </p>

          <div className="flex flex-wrap gap-5">
            <button
              onClick={() => navigate("explore")}
              className="btn-vivid px-12 py-5 text-base"
            >
              Explore Library <ChevronRight size={20} />
            </button>

            <button
              onClick={() => navigate("pricing")}
              className="px-12 py-5 rounded-full border border-white/30 hover:bg-white/10 transition-all font-bold uppercase text-sm tracking-widest text-white"
            >
              View Pricing
            </button>
          </div>
        </div>
      </section>

      <section className="py-24 px-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-end mb-16">
            <div>
              <h2 className="text-5xl mb-3 tracking-tight">
                Explore by Category
              </h2>
              <p className="text-gray-500 font-semibold">
                Curated by our expert visual editors
              </p>
            </div>
            <button
              onClick={() => navigate("explore")}
              className="text-blue-400 hover:text-blue-300 flex items-center gap-2 font-bold uppercase text-xs tracking-widest"
            >
              View all <ChevronRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {CATEGORIES.map((cat, i) => (
              <div
                key={cat}
                className="group cursor-pointer text-center"
                onClick={() => navigate("explore", cat)}
              >
                <div className="h-48 rounded-3xl mb-5 overflow-hidden border border-gray-200 group-hover:border-white/40 transition-all bg-gradient-to-br from-gray-800 to-gray-900 shadow-xl">
                  <img
                    src={`${CATEGORY_IMAGES[cat]}?auto=format&fit=crop&w=800&q=80`}
                    alt={cat}
                    className="w-full h-full object-cover group-hover:opacity-100 group-hover:scale-110 transition-all duration-700"
                  />
                </div>
                <span className="font-bold text-gray-500 group-hover:text-gray-900 transition-colors uppercase text-xs tracking-widest">
                  {cat}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-10 bg-gradient-to-b from-transparent to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto glass-morphism rounded-[60px] p-16 md:p-28 flex flex-col md:flex-row items-center gap-20">
          <div className="flex-1">
            <h2 className="text-6xl mb-8 leading-tight">
              BECOME A <span className="text-pink-500 italic">VIVID</span>{" "}
              CONTRIBUTOR
            </h2>
            <p className="text-gray-200 text-xl mb-10 leading-relaxed font-medium">
              Upload your high-quality imagery and start earning 40% royalties
              on every sale. Join 15,000+ artists worldwide.
            </p>
            <ul className="space-y-5 mb-12">
              <li className="flex items-center gap-4 text-gray-300 font-bold text-sm uppercase">
                <CheckCircle className="text-green-500" size={22} /> Global
                distribution network
              </li>
              <li className="flex items-center gap-4 text-gray-300 font-bold text-sm uppercase">
                <CheckCircle className="text-green-500" size={22} /> Advanced
                analytics dashboard
              </li>
              <li className="flex items-center gap-4 text-gray-300 font-bold text-sm uppercase">
                <CheckCircle className="text-green-500" size={22} /> Fast,
                monthly payouts
              </li>
            </ul>
            <button
              onClick={() => {
                if (user) {
                  navigate("upload");
                } else {
                  navigate("auth");
                }
              }}
              className="btn-vivid px-10 py-4"
            >
              Start Uploading
            </button>
          </div>
          <div className="flex-1 relative">
            <div className="rainbow-border p-1.5 shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&w=800&q=80"
                className="rounded-xl w-full"
                alt="Photography"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const ExplorePage = () => (
    <div className="pt-36 px-10 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-10 mb-16">
          <div>
            <h1 className="text-5xl mb-3 tracking-tighter">Explore Assets</h1>
            <p className="text-gray-500 font-semibold">
              Browse through millions of high-resolution images
            </p>
          </div>
          <div className="flex gap-5 w-full md:w-auto">
            <div className="flex-1 md:w-96 glass-morphism rounded-full flex items-center px-6 py-3 border border-gray-200 shadow-lg">
              <Search size={18} className="text-gray-500 mr-3" />

              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search colors, themes..."
                className="bg-transparent border-none outline-none text-sm w-full font-bold"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="glass-morphism rounded-full px-6 py-3 flex items-center gap-3 border border-gray-200 hover:bg-white/10 transition-all font-black uppercase text-[10px] tracking-widest"
            >
              <Filter size={18} />
              <span>Filter</span>
            </button>
          </div>
        </div>

        <div className="flex gap-5 mb-12 overflow-x-auto pb-4 scrollbar-hide">
          <button
            onClick={() => setActiveCategory("All")}
            className={`px-8 py-3 rounded-full text-xs font-black uppercase tracking-widest shadow-lg transition-all
      ${
        activeCategory === "All"
          ? "bg-black text-white"
          : "bg-gray-100 border border-gray-200 hover:bg-gray-200"
      }`}
          >
            All
          </button>

          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-8 py-3 rounded-full text-xs font-black uppercase tracking-widest whitespace-nowrap transition-all
        ${
          activeCategory === cat
            ? "bg-black text-white"
            : "bg-gray-100 border border-gray-200 hover:bg-gray-200"
        }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 mb-28">
          {filteredImages.map((img) => (
            <div
              key={img.id}
              className="image-card cursor-pointer"
              onClick={() => setSelectedImage(img)}
            >
              <img
                src={img.url}
                alt={img.name}
                className="w-full h-80 object-cover"
              />
              <div className="image-overlay">
                <div className="flex justify-between items-end">
                  <div>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-pink-500 font-black mb-2 block">
                      {img.category}
                    </span>
                    <h3 className="text-xl font-black text-white uppercase tracking-tight">
                      {img.name}
                    </h3>
                    <p className="text-xs text-gray-400 mt-1">
                      Uploaded by {img.uploadedBy?.name}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSave(img);
                      }}
                      className={`w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-lg ${
                        user?.savedImages?.some((i) => i.id === img.id)
                          ? "bg-red-500 text-white"
                          : "bg-white text-gray-900"
                      }`}
                    >
                      <Heart
                        size={20}
                        fill={
                          user?.savedImages?.some((i) => i.id === img.id)
                            ? "currentColor"
                            : "none"
                        }
                      />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadImage(img);
                      }}
                      className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-500 transition-all shadow-lg"
                    >
                      <Download size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const ImageDetailsModal = ({ img }) => {
    const [selectedSub, setSelectedSub] = useState("single");
    const isInCart = cart.some((i) => i.id === img.id);

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
        <div
          className="absolute inset-0 bg-white backdrop-blur-xl"
          onClick={() => setSelectedImage(null)}
        ></div>
        <div className="relative w-full max-w-7xl glass-morphism rounded-[40px] overflow-hidden shadow-2xl flex flex-col md:flex-row max-h-[95vh] border-white/20">
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-8 right-8 z-10 w-12 h-12 bg-white rounded-full flex items-center justify-center text-gray-900 hover:bg-white/20 transition-all shadow-lg"
          >
            <X size={28} />
          </button>

          <div className="flex-1 bg-black/30 flex items-center justify-center p-10 overflow-hidden">
            <img
              src={img.url}
              alt={img.name}
              className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl"
            />
          </div>

          <div className="w-full md:w-[450px] p-12 flex flex-col border-l border-gray-200 overflow-y-auto bg-white/40">
            <span className="text-[10px] uppercase tracking-[0.3em] text-blue-400 font-black mb-3">
              {img.category}
            </span>
            <h2 className="text-4xl mb-6 tracking-tighter leading-none">
              {img.name}
            </h2>
            <div className="text-xs text-gray-500 mt-4">
              Contributor: <strong>{img.uploadedBy?.name}</strong>
            </div>

            <div className="grid grid-cols-2 gap-5 mb-10 text-[10px] font-black uppercase tracking-widest">
              <div className="bg-gray-100 p-4 rounded-2xl border border-white/5 shadow-inner">
                <p className="text-gray-500 mb-1">Dimensions</p>
                <p className="text-sm">{img.dimensions}</p>
              </div>
              <div className="bg-gray-100 p-4 rounded-2xl border border-white/5 shadow-inner">
                <p className="text-gray-500 mb-1">Format</p>
                <p className="text-sm">{img.format}</p>
              </div>
              <div className="bg-gray-100 p-4 rounded-2xl border border-white/5 shadow-inner">
                <p className="text-gray-500 mb-1">Published</p>
                <p className="text-sm">{img.date}</p>
              </div>
              <div className="bg-gray-100 p-4 rounded-2xl border border-white/5 shadow-inner">
                <p className="text-gray-500 mb-1">License</p>
                <p className="text-sm">Standard</p>
              </div>
            </div>
            <div className="mb-10">
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] mb-5 flex items-center gap-3 text-gray-900">
                <Zap size={18} className="text-yellow-500" /> Choose License
              </h4>
              <div className="space-y-4">
                <div
                  onClick={() => setSelectedSub("single")}
                  className={`p-6 rounded-[24px] border-2 cursor-pointer transition-all ${
                    selectedSub === "single"
                      ? "bg-white/10 border-blue-500 shadow-lg"
                      : "bg-transparent border-gray-200"
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-black uppercase tracking-widest">
                      Single Asset
                    </span>
                    <span className="text-2xl font-black">${img.price}</span>
                  </div>
                </div>
                <div
                  onClick={() => setSelectedSub("pro")}
                  className={`p-6 rounded-[24px] border-2 cursor-pointer transition-all ${
                    selectedSub === "pro"
                      ? "bg-white/10 border-purple-500 shadow-lg"
                      : "bg-transparent border-gray-200"
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-xs font-black uppercase tracking-widest">
                        Pro Subscription
                      </span>
                      <p className="text-[9px] text-gray-500 mt-1 font-bold">
                        50 downloads / mo
                      </p>
                    </div>
                    <span className="text-2xl font-black">$49/mo</span>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-gray-500 text-[10px] mb-10 italic font-semibold leading-relaxed">
              * By purchasing, you agree to the VIVIDLENS standard license terms
              for all usage types.
            </p>
            <button
              onClick={() => {
                if (isInCart) {
                  setSelectedImage(null);
                  navigate("cart");
                } else {
                  addToCart(img);
                }
              }}
              className="w-full btn-vivid py-5 text-sm"
            >
              {isInCart ? "Go to Cart" : "Add to Cart"}
            </button>
            <button
              onClick={() => downloadImage(img)}
              className="w-full mt-4 bg-gray-100 border border-gray-200 rounded-full py-4 font-black uppercase text-xs tracking-widest hover:bg-gray-200"
            >
              Download Preview
            </button>
          </div>
        </div>
      </div>
    );
  };

  const PricingPage = () => (
    <div className="pt-40 px-10 pb-28">
      <div className="max-w-7xl mx-auto text-center mb-20">
        <h1 className="text-7xl mb-6 tracking-tighter">
          Plans for every <span className="vivid-text">Workflow</span>
        </h1>
        <p className="text-gray-500 max-w-2xl mx-auto font-bold text-lg">
          Flexible licensing that grows with your team. No hidden fees, cancel
          anytime.
        </p>
      </div>

      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
        {PRICING_PLANS.map((plan, i) => (
          <div
            key={i}
            className={`relative glass-morphism p-12 rounded-[50px] border border-gray-200 flex flex-col shadow-2xl ${
              plan.popular ? "ring-4 ring-purple-500" : ""
            }`}
          >
            {plan.popular && (
              <span className="absolute -top-5 left-1/2 -translate-x-1/2 bg-purple-500 text-gray-900 text-[10px] font-black uppercase px-6 py-2 rounded-full tracking-[0.2em] shadow-lg">
                Most Popular
              </span>
            )}
            <h3 className="text-3xl mb-3 uppercase tracking-tighter">
              {plan.name}
            </h3>
            <div className="mb-10">
              <span className="text-6xl font-black">${plan.price}</span>
              <span className="text-gray-500 font-bold ml-2">/MO</span>
            </div>
            <ul className="space-y-6 mb-12 flex-1">
              {plan.features.map((feat, j) => (
                <li
                  key={j}
                  className="flex items-center gap-4 text-xs text-gray-400 font-bold uppercase tracking-widest"
                >
                  <CheckCircle size={18} className="text-blue-400" /> {feat}
                </li>
              ))}
            </ul>
            <button
              className={`w-full py-5 rounded-2xl font-black uppercase text-xs tracking-widest transition-all shadow-lg ${
                plan.popular
                  ? "bg-white text-black hover:bg-gray-200"
                  : "bg-gray-100 hover:bg-white/10 border border-gray-200"
              }`}
            >
              Get Started
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  const AboutPage = () => (
    <div className="pt-40 px-10 pb-28">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-7xl mb-10 tracking-tighter">
          About <span className="vivid-text">VividLens</span>
        </h1>

        <p className="text-lg text-gray-600 leading-relaxed mb-16 font-medium">
          VividLens is a next-generation digital marketplace built for creators,
          designers, brands, and visual storytellers who demand more than
          ordinary stock imagery. Our platform was created to bridge the gap
          between creativity and accessibility—empowering photographers to
          monetize their work while giving users instant access to premium,
          high-resolution visual assets. From cinematic portraits and cyberpunk
          aesthetics to nature, architecture, and abstract visuals, VividLens is
          designed to inspire bold ideas and meaningful storytelling across the
          world.
          <br />
          <br />
          Unlike traditional stock platforms, VividLens focuses on quality,
          creator freedom, and fair earnings. Every image uploaded is curated to
          maintain artistic integrity, technical excellence, and commercial
          usability. Whether you are a startup founder, UI designer, filmmaker,
          content creator, or marketing professional, VividLens provides visuals
          that elevate your brand identity and creative output.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-20">
          {[
            { label: "Active Users", value: "120,000+" },
            { label: "Contributors", value: "15,000+" },
            { label: "Total Assets", value: "40M+" },
            { label: "Downloads", value: "300M+" },
          ].map((item, i) => (
            <div
              key={i}
              className="glass-morphism p-10 rounded-[40px] text-center border border-gray-200 shadow-xl"
            >
              <p className="text-4xl font-black mb-2">{item.value}</p>
              <p className="text-xs uppercase tracking-widest font-bold text-gray-500">
                {item.label}
              </p>
            </div>
          ))}
        </div>

        <h2 className="text-5xl mb-6 tracking-tight">
          For <span className="vivid-text">Users</span>
        </h2>
        <p className="text-gray-600 leading-relaxed mb-16 font-medium text-lg">
          Normal users on VividLens can explore, preview, save, and download
          world-class visual assets for personal or commercial use. With
          advanced filtering, curated collections, and flexible licensing
          options, users can quickly find the perfect image for websites,
          applications, social media, advertising campaigns, and creative
          projects. Our intuitive interface ensures a seamless experience from
          discovery to download.
        </p>

        <h2 className="text-5xl mb-6 tracking-tight">
          For <span className="vivid-text">Contributors</span>
        </h2>
        <p className="text-gray-600 leading-relaxed mb-20 font-medium text-lg">
          Contributors are the heart of VividLens. Photographers, digital
          artists, and creators can upload their work, track performance
          analytics, and earn up to 40% royalties on every sale. We provide
          global exposure, transparent payouts, and creative freedom—allowing
          artists to focus on what they do best: creating exceptional visuals
          that move the world.
        </p>

        <div className="glass-morphism p-16 rounded-[50px] border border-gray-200 text-center shadow-2xl">
          <h3 className="text-4xl font-black mb-6">
            Built for the{" "}
            <span className="vivid-text">Future of Creativity</span>
          </h3>
          <p className="text-gray-600 max-w-3xl mx-auto leading-relaxed font-medium">
            VividLens is more than a marketplace—it is a creative ecosystem.
            Designed with performance, elegance, and scalability in mind, our
            mission is to empower visual creators and fuel innovation across the
            digital world.
          </p>
        </div>
      </div>
    </div>
  );

  const ContributorProgramPage = () => (
    <div className="pt-40 px-10 pb-28">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-7xl mb-10 tracking-tighter">
          Contributor <span className="vivid-text">Program</span>
        </h1>

        <p className="text-lg text-gray-600 leading-relaxed mb-16 font-medium">
          The VividLens Contributor Program is designed for photographers,
          designers, and visual artists who want to showcase their creativity
          while earning real income. We empower creators with global exposure,
          fair royalties, and a powerful platform that values originality and
          quality. Every submission is carefully curated to ensure your work
          reaches professionals, brands, and creators worldwide.
          <br />
          <br />
          Whether you are an emerging artist or an established professional,
          VividLens gives you the tools, analytics, and audience you need to
          grow your creative career sustainably.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {[
            { title: "40% Royalties", desc: "Earn on every download" },
            { title: "Global Reach", desc: "Millions of users worldwide" },
            { title: "Monthly Payouts", desc: "Fast & transparent payments" },
          ].map((item, i) => (
            <div
              key={i}
              className="glass-morphism p-12 rounded-[40px] border border-gray-200 shadow-xl"
            >
              <h3 className="text-3xl font-black mb-4">{item.title}</h3>
              <p className="text-gray-500 font-medium">{item.desc}</p>
            </div>
          ))}
        </div>

        <h2 className="text-5xl mb-8 tracking-tight">
          How It <span className="vivid-text">Works</span>
        </h2>

        <ul className="space-y-8 mb-20">
          {[
            "Create your free contributor account",
            "Upload high-quality original images",
            "Get reviewed by our curation team",
            "Start earning when users download your work",
          ].map((step, i) => (
            <li
              key={i}
              className="glass-morphism p-8 rounded-[32px] border border-gray-200 shadow-lg text-lg font-semibold"
            >
              {i + 1}. {step}
            </li>
          ))}
        </ul>

        <div className="glass-morphism p-16 rounded-[50px] border border-gray-200 text-center shadow-2xl">
          <h3 className="text-4xl font-black mb-6">
            Turn Creativity Into <span className="vivid-text">Income</span>
          </h3>
          <p className="text-gray-600 max-w-2xl mx-auto mb-10 font-medium">
            Join thousands of contributors already earning on VividLens. Upload
            your work, grow your audience, and get rewarded for your creativity.
          </p>
          <button
            onClick={() => navigate("upload")}
            className="btn-vivid px-14 py-5"
          >
            Start Contributing
          </button>
        </div>
      </div>
    </div>
  );

  const PopularCollectionsPage = () => (
    <div className="pt-40 px-10 pb-28">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-7xl mb-10 tracking-tighter">
          Popular <span className="vivid-text">Collections</span>
        </h1>

        <p className="text-lg text-gray-600 leading-relaxed mb-16 font-medium">
          Discover the most downloaded and trending collections on VividLens.
          These handpicked visuals are loved by designers, brands, and creators
          worldwide for their quality, creativity, and commercial value.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {CATEGORIES.map((cat) => (
            <div
              key={cat}
              onClick={() => navigate("explore")}
              className="glass-morphism p-12 rounded-[40px] border border-gray-200 shadow-xl cursor-pointer hover:scale-105 transition-all"
            >
              <h3 className="text-2xl font-black mb-3">{cat}</h3>
              <p className="text-gray-500 font-medium">
                Explore top-performing {cat.toLowerCase()} assets.
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const FreeAssetsPage = () => (
    <div className="pt-40 px-10 pb-28">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-7xl mb-10 tracking-tighter">
          Free <span className="vivid-text">Assets</span>
        </h1>

        <p className="text-lg text-gray-600 leading-relaxed mb-16 font-medium">
          VividLens offers a curated selection of free, high-quality images for
          personal and commercial use. These assets are perfect for startups,
          students, and creators who want premium visuals without cost.
        </p>

        <button
          onClick={() => navigate("explore")}
          className="btn-vivid px-12 py-5"
        >
          Browse Free Assets
        </button>
      </div>
    </div>
  );

  const TrendingNowPage = () => (
    <div className="pt-40 px-10 pb-28">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-7xl mb-10 tracking-tighter">
          Trending <span className="vivid-text">Now</span>
        </h1>

        <p className="text-lg text-gray-600 leading-relaxed mb-16 font-medium">
          Stay ahead of creative trends. These assets are currently trending
          across industries, social media, branding, and digital campaigns.
        </p>

        <button
          onClick={() => navigate("explore")}
          className="btn-vivid px-12 py-5"
        >
          Explore Trending Assets
        </button>
      </div>
    </div>
  );


  const ApiAccessPage = () => (
    <div className="pt-40 px-10 pb-28">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-7xl mb-10 tracking-tighter">
          API <span className="vivid-text">Access</span>
        </h1>

        <p className="text-lg text-gray-600 leading-relaxed mb-16 font-medium">
          The VividLens API allows developers and businesses to integrate
          millions of high-quality assets directly into their applications,
          websites, and workflows. Build faster, scale effortlessly, and deliver
          stunning visuals with ease.
        </p>

        <ul className="space-y-6 mb-16">
          <li className="font-semibold">• RESTful API</li>
          <li className="font-semibold">• Secure authentication</li>
          <li className="font-semibold">• High-speed content delivery</li>
          <li className="font-semibold">• Commercial licensing</li>
        </ul>

        <button className="btn-vivid px-14 py-5">Request API Access</button>
      </div>
    </div>
  );


  const DashboardPage = () => (
    <div className="pt-40 px-10 pb-28">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center gap-10 mb-20 bg-gray-100 p-12 rounded-[50px] border border-white/5 shadow-2xl">
          <div
            onClick={() => setCurrentPage("profile")}
            className="w-40 h-40 rounded-full overflow-hidden border-8 border-purple-500 shadow-2xl cursor-pointer"
          >
            {user.avatar ? (
              <img
                src={user.avatar}
                alt="Avatar"
                className="w-full h-full object-cover"
              />
            ) : (
              <UserCircleIcon className="w-full h-full text-gray-400" />
            )}
          </div>

          <div className="text-center md:text-left flex-1">
            <h1 className="text-5xl mb-2 tracking-tighter">{user?.name}</h1>
            <p className="text-gray-500 mb-8 font-black uppercase tracking-widest text-xs">
              {user?.email}
            </p>
            <div className="flex justify-center md:justify-start gap-6">
              <div className="bg-black/50 px-8 py-4 rounded-[24px] border border-white/5 shadow-inner">
                <p className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">
                  Uploads
                </p>
                <p className="text-3xl font-black">
                  {myImages.length}
                </p>
              </div>
              <div className="bg-black/50 px-8 py-4 rounded-[24px] border border-white/5 shadow-inner">
                <p className="text-[10px] text-gray-500 uppercase font-black tracking-widest mb-1">
                  Downloads
                </p>
                <p className="text-3xl font-black">{user?.downloads ?? 0}</p>
              </div>
            </div>
          </div>
          <button
            onClick={() => {
              localStorage.removeItem("token");
              setUser(null);
              setAuthEmail("");
              setAuthPassword("");
              setAuthName("");
              setAuthError("");
              setAuthMode("login");
              setCurrentPage("home");
            }}
            className="text-gray-500 hover:text-red-500 flex items-center gap-3 text-xs font-black uppercase tracking-widest transition-colors"
          >
            <LogOut size={20} /> Logout
          </button>
        </div>

        <div className="mb-12">
          <div className="flex gap-12 border-b border-gray-200 mb-12">
            <button
              onClick={() => setActiveTab("gallery")}
              className={`font-black uppercase tracking-widest text-xs pb-6 ${
                activeTab === "gallery"
                  ? "text-gray-900 border-b-4 border-blue-500"
                  : "text-gray-600"
              }`}
            >
              My Gallery
            </button>

            <button
              onClick={() => setActiveTab("saved")}
              className={`font-black uppercase tracking-widest text-xs pb-6 ${
                activeTab === "saved"
                  ? "text-gray-900 border-b-4 border-blue-500"
                  : "text-gray-600"
              }`}
            >
              Saved Items
            </button>

            <button
              className="text-gray-600 hover:text-gray-900 font-black uppercase tracking-widest text-xs pb-6 transition-all"
              onClick={() => setCurrentPage("payments")}
            >
              Payments
            </button>
          </div>

          {activeTab === "gallery" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-8">
              {myImages.length === 0 ? (
                <p className="text-gray-400">No uploads yet</p>
              ) : (
                myImages.map((img) => (
                  <img
                    key={img._id}
                    src={img.imageUrl}
                    alt={img.title}
                    className="w-full h-40 object-cover rounded"
                  />
                ))
              )}
            </div>
          )}

          {activeTab === "saved" && (
            <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-8">
              {(user?.savedImages?.length ?? 0) === 0 ? (
                <p className="text-gray-500 font-bold">
                  No saved images yet ❤️
                </p>
              ) : (
                user.savedImages.map((img) => (
                  <div
                    key={img.id}
                    className="h-72 rounded-[32px] overflow-hidden shadow-xl cursor-pointer"
                    onClick={() => setSelectedImage(img)}
                  >
                    <img
                      src={img.url}
                      alt={img.name}
                      className="w-full h-full object-cover hover:scale-110 transition-all duration-700"
                    />
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const UploadPage = () => {
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState(CATEGORIES[0]);
    const [status, setStatus] = useState("idle");
    // FIX 5: Added local price and dimensions state to UploadPage
    // to avoid referencing outer scope variables that conflict
    const [localPrice, setLocalPrice] = useState("");
    const [localDimensions, setLocalDimensions] = useState("");

    const handleSubmit = (e) => {
      e.preventDefault();
      setStatus("uploading");
      setTimeout(() => {
        handleUpload({
          name: title,
          category,
          price: localPrice || 25,
          dimensions: localDimensions || "6000x4000",
          format: "JPG",
          url: URL.createObjectURL(file),
        });
        setStatus("success");
      }, 2000);
    };

    return (
      <div className="pt-40 px-10 pb-28">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-6xl mb-12 tracking-tighter leading-none">
            UPLOAD TO <span className="vivid-text">MARKETPLACE</span>
          </h1>

          {status === "success" ? (
            <div className="glass-morphism p-20 rounded-[50px] text-center border-white/20 shadow-2xl">
              <div className="w-24 h-24 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-inner">
                <CheckCircle size={48} />
              </div>
              <h2 className="text-4xl font-black mb-6 uppercase tracking-tight">
                Upload Success!
              </h2>
              <p className="text-gray-500 font-bold mb-10 text-lg">
                Your asset is being reviewed by our curators.
              </p>
              <button
                onClick={() => {
                  setStatus("idle");
                  setFile(null);
                }}
                className="btn-vivid px-12 py-5 mx-auto"
              >
                Upload Another
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="glass-morphism p-12 md:p-16 rounded-[60px] space-y-10 shadow-2xl"
            >
              <div
                className={`border-4 border-dashed rounded-[40px] p-16 text-center transition-all ${
                  file
                    ? "border-green-500 bg-green-500/5"
                    : "border-gray-200 hover:border-blue-500"
                }`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  setFile(e.dataTransfer.files[0]);
                }}
              >
                {file ? (
                  <div className="animate-in fade-in duration-500">
                    <img
                      src={URL.createObjectURL(file)}
                      alt="Preview"
                      className="w-56 h-40 object-cover mx-auto rounded-3xl mb-6 shadow-2xl"
                    />
                    <p className="text-sm font-black uppercase tracking-widest">
                      {file.name}
                    </p>
                    <button
                      type="button"
                      onClick={() => setFile(null)}
                      className="text-red-500 text-[10px] font-black uppercase tracking-widest mt-4 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto text-gray-700 mb-8" size={64} />
                    <p className="text-2xl font-black mb-3 uppercase tracking-tight">
                      Drop your masterpiece
                    </p>
                    <p className="text-xs text-gray-500 mb-10 font-bold uppercase tracking-widest">
                      RAW, JPG, TIFF up to 100MB
                    </p>
                    <label className="bg-white text-black px-10 py-4 rounded-full text-xs font-black uppercase tracking-widest cursor-pointer hover:bg-gray-200 transition-all shadow-xl">
                      Select File
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => setFile(e.target.files[0])}
                      />
                    </label>
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="text-[10px] text-gray-500 font-black uppercase tracking-widest ml-1">
                    Asset Title
                  </label>
                  <input
                    type="text"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="E.G. NEON STREETS"
                    className="w-full bg-gray-100 border-2 border-gray-200 rounded-2xl px-6 py-4 outline-none focus:border-blue-500 transition-all font-bold uppercase text-sm"
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] text-gray-500 font-black uppercase tracking-widest ml-1">
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full bg-[#fff] border-2 border-gray-200 rounded-2xl px-6 py-4 outline-none focus:border-blue-500 font-bold uppercase text-sm"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] text-gray-500 font-black uppercase tracking-widest ml-1">
                    Price
                  </label>
                  {/* FIX 6: Added className to price and dimensions inputs (were unstyled) */}
                  <input
                    type="number"
                    placeholder="Price"
                    value={localPrice}
                    onChange={(e) => setLocalPrice(e.target.value)}
                    className="w-full bg-gray-100 border-2 border-gray-200 rounded-2xl px-6 py-4 outline-none focus:border-blue-500 transition-all font-bold text-sm"
                  />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] text-gray-500 font-black uppercase tracking-widest ml-1">
                    Dimensions
                  </label>
                  <input
                    type="text"
                    placeholder="Dimensions"
                    value={localDimensions}
                    onChange={(e) => setLocalDimensions(e.target.value)}
                    className="w-full bg-gray-100 border-2 border-gray-200 rounded-2xl px-6 py-4 outline-none focus:border-blue-500 transition-all font-bold text-sm"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-[10px] text-gray-500 font-black uppercase tracking-widest ml-1">
                  Metadata & Keywords
                </label>
                <textarea
                  rows="4"
                  placeholder="ADD TAGS FOR BETTER DISCOVERABILITY..."
                  className="w-full bg-gray-100 border-2 border-gray-200 rounded-2xl px-6 py-4 outline-none focus:border-blue-500 font-bold uppercase text-sm"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={!file || status === "uploading"}
                className={`w-full py-6 rounded-[24px] font-black uppercase tracking-widest transition-all shadow-2xl ${
                  !file ? "bg-gray-900 text-gray-600" : "btn-vivid"
                }`}
              >
                {status === "uploading"
                  ? "Publishing..."
                  : "Publish to VividLens"}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  };

  const CartPage = () => {
    const subtotal = cart.reduce((acc, item) => acc + item.price, 0);
    const tax = subtotal * 0.1;
    const total = subtotal + tax;

    return (
      <div className="pt-40 px-10 pb-28">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-6xl mb-16 tracking-tighter">
            YOUR <span className="vivid-text">CURATION</span> ({cart.length})
          </h1>

          {cart.length === 0 ? (
            <div className="text-center py-32 bg-gray-100 rounded-[60px] border-4 border-dashed border-gray-200 shadow-2xl">
              <ShoppingCart size={80} className="text-gray-800 mx-auto mb-10" />
              <h2 className="text-3xl font-black mb-8 uppercase tracking-tight">
                Your cart is empty
              </h2>
              <button
                onClick={() => navigate("explore")}
                className="btn-vivid px-10 py-4 mx-auto"
              >
                Browse Library
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
              <div className="lg:col-span-2 space-y-8">
                {cart.map((item) => (
                  <div
                    key={item.id}
                    className="glass-morphism p-8 rounded-[32px] flex items-center gap-10 border border-gray-200 shadow-xl group"
                  >
                    <img
                      src={item.url}
                      alt={item.name}
                      onClick={() => setSelectedImage(item)}
                      className="w-48 h-32 object-cover rounded-2xl shadow-lg group-hover:scale-105 transition-transform"
                    />
                    <div className="flex-1">
                      <h3 className="text-2xl font-black uppercase tracking-tight mb-2">
                        {item.name}
                      </h3>
                      <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest">
                        {item.category} • STANDARD LICENSE
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-black mb-4 tracking-tighter">
                        ${item.price}
                      </p>
                      <button
                        onClick={() =>
                          setCart(cart.filter((i) => i.id !== item.id))
                        }
                        className="text-red-500 text-[10px] font-black uppercase tracking-widest hover:underline"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="lg:col-span-1">
                <div className="glass-morphism p-10 rounded-[50px] border border-gray-200 sticky top-40 shadow-2xl">
                  <h3 className="text-xl font-black uppercase tracking-widest mb-10">
                    Order Summary
                  </h3>
                  <div className="space-y-6 mb-12">
                    <div className="flex justify-between text-gray-500 text-xs font-bold uppercase tracking-widest">
                      <span>Subtotal</span>
                      <span>${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-500 text-xs font-bold uppercase tracking-widest">
                      <span>Platform Tax (10%)</span>
                      <span>${tax.toFixed(2)}</span>
                    </div>
                    <div className="pt-8 border-t border-gray-200 flex justify-between text-4xl font-black tracking-tighter">
                      <span>Total</span>
                      <span className="vivid-text">${total.toFixed(2)}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate("checkout")}
                    className="w-full btn-vivid py-6 text-sm"
                  >
                    Checkout Now
                  </button>
                  <p className="text-center text-[9px] text-gray-600 mt-8 font-black uppercase tracking-[0.2em]">
                    SECURE 256-BIT ENCRYPTED TRANSACTION
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const CheckoutPage = () => {
    const total = cart.reduce((acc, item) => acc + item.price, 0);
    const [method, setMethod] = useState("card");

    if (paymentStatus === "verifying") {
      return (
        <div className="pt-40 flex items-center justify-center min-h-[70vh]">
          <div className="text-center">
            <div className="loader-bar w-64 mx-auto mb-6"></div>
            <h1 className="text-4xl font-black mb-4">Verifying Payment...</h1>
            <p className="text-gray-500 font-bold">
              Please wait while we confirm your transaction
            </p>
          </div>
        </div>
      );
    }

    if (paymentStatus === "success") {
      return (
        <div className="pt-40 flex items-center justify-center min-h-[70vh]">
          <div className="text-center">
            <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={48} className="text-white" />
            </div>
            <h1 className="text-5xl font-black mb-4">Payment Successful 🎉</h1>
            <p className="text-gray-500 font-bold">
              Redirecting you to explore new assets...
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="pt-40 px-10 pb-28">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
            <div>
              <h1 className="text-5xl mb-12 tracking-tighter leading-none">
                SECURE <span className="vivid-text">PAYMENT</span>
              </h1>

              <div className="space-y-10 mb-16">
                <div className="space-y-4">
                  <label className="text-[10px] text-gray-500 font-black uppercase tracking-widest ml-1">
                    Method
                  </label>
                  <div className="grid grid-cols-2 gap-5">
                    <button
                      onClick={() => setMethod("card")}
                      className={`p-6 rounded-3xl border-2 flex items-center justify-center gap-4 transition-all shadow-lg ${
                        method === "card"
                          ? "bg-white/10 border-blue-500"
                          : "bg-transparent border-gray-200"
                      }`}
                    >
                      <CreditCard size={24} />{" "}
                      <span className="text-xs font-black uppercase tracking-widest">
                        Card
                      </span>
                    </button>
                    <button
                      onClick={() => setMethod("upi")}
                      className={`p-6 rounded-3xl border-2 flex items-center justify-center gap-4 transition-all shadow-lg ${
                        method === "upi"
                          ? "bg-white/10 border-blue-500"
                          : "bg-transparent border-gray-200"
                      }`}
                    >
                      <Smartphone size={24} />{" "}
                      <span className="text-xs font-black uppercase tracking-widest">
                        UPI / QR
                      </span>
                    </button>
                  </div>
                </div>

                {method === "card" ? (
                  <div className="space-y-6 animate-in slide-in-from-bottom duration-500">
                    <input
                      type="text"
                      placeholder="CARD NUMBER"
                      className="w-full bg-gray-100 border-2 border-gray-200 rounded-2xl px-6 py-5 outline-none font-bold uppercase text-sm tracking-widest"
                    />
                    <div className="grid grid-cols-2 gap-6">
                      <input
                        type="text"
                        placeholder="EXPIRY MM/YY"
                        className="w-full bg-gray-100 border-2 border-gray-200 rounded-2xl px-6 py-5 outline-none font-bold uppercase text-sm tracking-widest"
                      />
                      <input
                        type="text"
                        placeholder="CVV"
                        className="w-full bg-gray-100 border-2 border-gray-200 rounded-2xl px-6 py-5 outline-none font-bold uppercase text-sm tracking-widest"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="p-12 border-4 border-dashed border-gray-200 rounded-[40px] text-center shadow-inner animate-in zoom-in duration-500">
                    <div className="w-40 h-40 bg-white mx-auto mb-6 rounded-[24px] p-3 shadow-2xl">
                      <img
                        src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=vividlens-payment"
                        alt="QR Code"
                        className="w-full h-full"
                      />
                    </div>
                    <p className="text-[10px] text-gray-500 font-black uppercase tracking-widest leading-relaxed">
                      Scan with your preferred UPI app
                      <br />
                      To initiate instant transfer
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="glass-morphism p-12 rounded-[60px] border border-white/20 h-fit shadow-2xl">
              <h3 className="text-2xl font-black uppercase tracking-widest mb-10 border-b border-gray-200 pb-6">
                Summary
              </h3>
              <div className="space-y-6 mb-12">
                {cart.map((i) => (
                  <div
                    key={i.id}
                    className="flex justify-between items-center text-xs font-bold uppercase tracking-widest"
                  >
                    <span className="text-gray-500">{i.name}</span>
                    <span className="font-black">${i.price}</span>
                  </div>
                ))}
                <div className="pt-10 border-t-2 border-gray-200 flex justify-between text-5xl font-black mt-10 tracking-tighter">
                  <span>TOTAL</span>
                  <span className="vivid-text">
                    ${cart.reduce((a, b) => a + b.price, 0).toFixed(2)}
                  </span>
                </div>
              </div>
              <button
                onClick={() => {
                  if (paymentStatus !== "idle") return;

                  setPaymentStatus("verifying");

                  setTimeout(() => {
                    addPayment({
                      amount: total,
                      date: new Date().toISOString(),
                      items: cart,
                    });
                    setCart([]);
                    setPaymentStatus("success");
                    setTimeout(() => {
                      setPaymentStatus("idle");
                      navigate("explore");
                    }, 3000);
                  }, 2000);
                }}
                className="w-full btn-vivid py-6 text-sm"
              >
                Confirm Payment
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };


  const ProfilePage = ({ user, setUser, setCurrentPage }) => {
    const [name, setName] = useState(user.name);
    const [email, setEmail] = useState(user.email);
    const [avatar, setAvatar] = useState(user.avatar);

    const saveProfile = () => {
      setUser((prev) => ({
        ...prev,
        name,
        email,
        avatar,
      }));
      setCurrentPage("dashboard");
    };

    return (
      <div className="pt-40 px-10 pb-28 min-h-screen">
        <div className="max-w-3xl mx-auto">
          <div className="glass-morphism p-12 md:p-16 rounded-[50px] border border-gray-200 shadow-2xl">
            <h1 className="text-4xl font-black mb-12 tracking-tighter">
              Edit <span className="vivid-text">Profile</span>
            </h1>
            <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-purple-500 mx-auto mb-10">
              {avatar ? (
                <img
                  src={avatar}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <UserCircleIcon className="w-full h-full text-gray-400" />
              )}
            </div>

            <div className="space-y-8">
              <div className="space-y-3">
                <label className="text-[10px] text-gray-500 font-black uppercase tracking-widest ml-1">
                  Name
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-gray-100 border-2 border-gray-200 rounded-2xl px-6 py-5 outline-none focus:border-blue-500 font-bold uppercase text-sm"
                />
              </div>

              <div className="space-y-3">
                <label className="text-[10px] text-gray-500 font-black uppercase tracking-widest ml-1">
                  Email
                </label>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-gray-100 border-2 border-gray-200 rounded-2xl px-6 py-5 outline-none focus:border-blue-500 font-bold uppercase text-sm"
                />
              </div>

              <label className="cursor-pointer text-center block">
                <span className="text-xs font-black uppercase tracking-widest text-purple-500">
                  Change Profile Image
                </span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) =>
                    setAvatar(URL.createObjectURL(e.target.files[0]))
                  }
                />
              </label>

              <div className="flex gap-6 pt-6">
                <button onClick={saveProfile} className="btn-vivid px-10 py-5">
                  Save Changes
                </button>

                <button
                  onClick={() => setCurrentPage("dashboard")}
                  className="px-10 py-5 rounded-full border border-gray-300 font-black uppercase text-xs tracking-widest hover:bg-gray-100 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // FIX 7: MyGallery was accessing user.uploads which doesn't exist in the data model
  // Changed to use myImages from parent scope via prop or fallback gracefully
  const MyGallery = ({ user }) => {
    const uploads = user?.uploads || [];
    return uploads.length === 0 ? (
      <p className="text-center mt-20">No uploads yet</p>
    ) : (
      <div className="grid grid-cols-3 gap-6 p-10">
        {uploads.map((img) => (
          <img key={img.id} src={img.url} alt={img.name || "upload"} />
        ))}
      </div>
    );
  };

  const AuthPage = () => (
    <div className="pt-48 px-10 flex items-center justify-center min-h-[90vh]">
      <div className="max-w-lg w-full glass-morphism p-12 md:p-16 rounded-[60px] border border-gray-200 shadow-2xl">
        <div className="flex gap-12 mb-12 border-b border-gray-200 pb-6">
          <button
            type="button"
            onClick={() => switchAuthMode("login")}
            className={`text-2xl font-black uppercase tracking-widest transition-all ${
              authMode === "login" ? "vivid-text" : "text-gray-700"
            }`}
          >
            Login
          </button>

          <button
            type="button"
            onClick={() => switchAuthMode("register")}
            className={`text-2xl font-black uppercase tracking-widest transition-all ${
              authMode === "register" ? "vivid-text" : "text-gray-700"
            }`}
          >
            Join
          </button>
        </div>

        {/* FIX 8: Form submit handler was correct but the submit button had type="button"
            which bypassed form onSubmit — fixed button to type="submit" */}
        <form
          onSubmit={authMode === "login" ? handleLogin : handleRegister}
          className="space-y-6"
        >
          <div
            className={`space-y-3 transition-all ${
              authMode === "register" ? "block" : "hidden"
            }`}
          >
            <label className="text-[10px] text-gray-500 font-black uppercase tracking-widest ml-1">
              Full Name
            </label>
            <input
              ref={nameInputRef}
              type="text"
              required={authMode === "register"}
              value={authName}
              onFocus={() => setActiveField("name")}
              onChange={(e) => setAuthName(e.target.value)}
              className="w-full bg-gray-100 border-2 border-gray-200 rounded-2xl px-6 py-5 outline-none focus:border-purple-500 font-bold uppercase text-sm tracking-widest"
            />
          </div>

          <div className="space-y-3">
            <label className="text-[10px] text-gray-500 font-black uppercase tracking-widest ml-1">
              Email Address
            </label>
            <input
              ref={emailInputRef}
              type="email"
              required
              value={authEmail}
              onFocus={() => setActiveField("email")}
              onChange={(e) => setAuthEmail(e.target.value)}
              placeholder="USER@gmail.com"
              className="w-full bg-gray-100 border-2 border-gray-200 rounded-2xl px-6 py-5 outline-none focus:border-purple-500 font-bold uppercase text-sm tracking-widest"
            />
          </div>
          <div className="space-y-3">
            <label className="text-[10px] text-gray-500 font-black uppercase tracking-widest ml-1">
              Secure Password
            </label>

            <input
              type="password"
              placeholder="Password"
              value={authPassword}
              onChange={(e) => setAuthPassword(e.target.value)}
              className="w-full px-6 py-4 rounded-full border"
              autoComplete="new-password"
            />
          </div>

          {authError && (
            <p className="text-red-500 text-sm mt-2 font-bold">{authError}</p>
          )}
          {/* FIX 8 continued: Changed type="button" to type="submit" so form onSubmit fires */}
          <button
            type="submit"
            className="btn-vivid w-full"
          >
            {authMode === "login" ? "Login" : "Join Free"}
          </button>
        </form>
        <p className="text-center text-[9px] text-gray-600 mt-12 font-black uppercase tracking-[0.2em] leading-relaxed">
          BY JOINING, YOU AGREE TO OUR
          <br />
          PRIVACY POLICIES AND CREATOR TERMS
        </p>
      </div>
    </div>
  );

  // FIX 9: SavedImagesPage was accessing user.savedImages without null-guard
  const SavedImagesPage = ({ user }) => {
    const savedImgs = user?.savedImages || [];
    return savedImgs.length === 0 ? (
      <p className="text-center mt-20">No saved images</p>
    ) : (
      <div className="grid grid-cols-3 gap-6 p-10">
        {savedImgs.map((img) => (
          <img key={img.id} src={img.url} alt={img.name || "saved"} />
        ))}
      </div>
    );
  };

  const PaymentsPage = () => (
    <div className="pt-40 px-10 pb-28">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-5xl mb-10 tracking-tighter">Payment History</h1>
        {/* FIX 10: payments could be undefined on user; added null-guard */}
        {(user?.payments?.length ?? 0) === 0 ? (
          <div className="bg-gray-100 p-20 rounded-[40px] text-center">
            <CreditCard size={64} className="mx-auto mb-6 text-gray-600" />
            <p className="font-black uppercase tracking-widest text-xs text-gray-500">
              No payments yet
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {user.payments.map((p, i) => (
              <div key={i} className="glass-morphism p-6 rounded-3xl space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-black uppercase text-xs">
                    Order #{i + 1}
                  </span>
                  <span className="font-black">${p.amount}</span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {p.items?.map((img) => (
                    <div key={img.id} className="rounded-xl overflow-hidden">
                      <img
                        src={img.url}
                        alt={img.name}
                        className="h-24 w-full object-cover"
                      />
                      <p className="text-[10px] font-bold mt-1 text-center">
                        {img.name}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      <CustomStyles />
      {isLoading && <div className="loader-bar w-full"></div>}
      <Header />
      <main className="transition-all duration-700">
        {currentPage === "home" && <HomePage />}
        {currentPage === "explore" && <ExplorePage />}
        {currentPage === "pricing" && <PricingPage />}
        {currentPage === "about" && <AboutPage />}
        {currentPage === "contributor" && <ContributorProgramPage />}
        {currentPage === "popular" && <PopularCollectionsPage />}
        {currentPage === "free" && <FreeAssetsPage />}
        {currentPage === "trending" && <TrendingNowPage />}
        {currentPage === "api" && <ApiAccessPage />}
        {currentPage === "auth" && <AuthPage />}
        {currentPage === "dashboard" && user && <DashboardPage />}
        {currentPage === "upload" && <UploadPage />}
        {currentPage === "cart" && <CartPage />}
        {currentPage === "checkout" && <CheckoutPage />}
        {currentPage === "ai" && (
          <AiGenerationPage
            aiPrompt={aiPrompt}
            setAiPrompt={setAiPrompt}
            aiImage={aiImage}
            aiLoading={aiLoading}
            user={user}
            generateAiImage={generateAiImage}
            navigate={navigate}
          />
        )}
        {currentPage === "profile" && user && (
          <ProfilePage
            user={user}
            setUser={setUser}
            setCurrentPage={setCurrentPage}
          />
        )}
        {currentPage === "saved" && user && <SavedImagesPage user={user} />}
        {currentPage === "gallery" && user && <MyGallery user={user} />}
        {currentPage === "payments" && <PaymentsPage />}
      </main>
      {selectedImage && <ImageDetailsModal img={selectedImage} />}
      <Footer />
    </div>
  );
}